import { Route, redirect, Outlet, Navigate } from "react-router";
import { Stack, useTheme, SvgIcon } from "@mui/material";
import { useState } from "react";
import { Login } from "./Login";
import { ForgotPassword } from "./ForgotPassword";
import { ResetPassword } from "./ResetPassword";
import { AuthWithToken } from "./AuthWithToken";
import { Footer } from "src/components/Footer";
import { loggingOutSession } from "src/api/axiosClient/userApi";
import { OAuthLogin } from "./OAuthLogin";
import { UserService } from "src/services/UserService";
import { NoCompany } from "./NoCompany";
import { Consents } from "./Consents";
import { CasLogin } from "src/pages/Authentication/CasLogin.tsx";
import { Saml2Login } from "src/pages/Authentication/Saml2Login.tsx";
import { useResponsiveBreakpoints } from "src/utils/useResponsiveBreakpoints";
import { useApplicationParameters } from "src/api/client-api/appParams/useApplicationParameters";
import { getThemeLogo } from "@foodpilot/foods";
import { buildLoginUrl, parseLoginReason } from "src/services/loginEntry";
import { RouteErrorPage } from "src/components/PageState/RouteErrorPage";

const logout = async ({ request }: { request: Request }) => {
    const url = new URL(request.url);
    if (url.pathname !== "/logout") {
        return null;
    }

    UserService.logout();

    try {
        await loggingOutSession.post("/api/token/invalidate");
    } catch (err) {
        console.warn("Failed to invalidate token - probably already expired");
    }

    return redirect(
        buildLoginUrl({
            reason: parseLoginReason(url.searchParams.get("reason")) ?? "logout",
            destination: url.searchParams.get("destination"),
        }),
    );
};

export const useAuthRouter = () => {
    return (
        <Route element={<AuthLayout />} errorElement={<RouteErrorPage canContactSupport={false} />}>
            <Route
                path="logout"
                loader={logout}
                element={
                    // I noticed sometimes the loader doesn't redirect to `/login` for some reasons.
                    // Doing it this way, I ensure if the page loads, it'll still redirect us to /login.
                    <Navigate to="/login" />
                }
            />
            <Route path="login" element={<Login />} />
            <Route path="consents" element={<Consents />} />
            <Route path="oauth/:providerName" element={<OAuthLogin />} />
            <Route path="cas3/:providerName" element={<CasLogin />} />
            <Route path="saml2/:providerName" element={<Saml2Login />} />
            <Route path="forgot-password" element={<ForgotPassword />} />
            <Route path="reset-password/:token" element={<ResetPassword />} />
            <Route path="auth" element={<AuthWithToken />} />
        </Route>
    );
};

export const useNoCompanyRouter = () => {
    return (
        <Route element={<AuthLayout />} errorElement={<RouteErrorPage />}>
            <Route path="noCompany" element={<NoCompany />} />
        </Route>
    );
};

const AuthLayout = () => {
    const theme = useTheme();
    const { applicationParameters } = useApplicationParameters();
    const [imageError, setImageError] = useState(false);
    const { isMobile, isTablet, isDesktop } = useResponsiveBreakpoints();
    const [logoError, setLogoError] = useState(false);

    // Récupération du logo du thème en cours
    const currentTheme = applicationParameters?.theme || "foodpilot";
    const ThemeLogo = getThemeLogo(currentTheme);

    const getImageSrc = () => {
        if (applicationParameters?.logo && !logoError) {
            return applicationParameters.logo;
        }
        return null; // Utilisera ThemeLogo comme composant SVG
    };

    const renderLogo = () => {
        const imageSrc = getImageSrc();

        if (imageSrc) {
            // Afficher l'image personnalisée
            return <img src={imageSrc} alt="logo" width="200" onError={handleImageError} />;
        } else {
            // Afficher le logo du thème
            return (
                <SvgIcon
                    component={ThemeLogo}
                    sx={{
                        width: "200px",
                        height: "auto",
                        flexShrink: "0",
                    }}
                    inheritViewBox
                />
            );
        }
    };

    const handleImageError = () => {
        setLogoError(true);
    };

    return (
        <Stack
            width="100%"
            minHeight="100%"
            alignItems="stretch"
            justifyContent="space-between"
            sx={{
                backgroundColor: theme.palette.primary[50],
                px: isMobile ? "1rem" : "80px",
                paddingTop: "52px",
            }}
        >
            <Stack flex={1} alignItems="center" justifyContent="center">
                <Stack
                    alignItems="center"
                    gap="32px"
                    width={
                        isMobile ? "100%"
                        : isTablet ?
                            "calc(100% / 2)"
                        :   "calc(100% / 3)"
                    }
                >
                    {renderLogo()}
                    <Outlet />
                </Stack>
            </Stack>
            <Footer />
        </Stack>
    );
};
